import { useApolloClient } from "@apollo/client";
import React from "react";
import { useUrlSearchParams } from "../commons/hooks/useUrlSearchParams.js";
import { REF_TOKEN_URL_SEARCH_PARAM } from "../constants.js";
import { InvitationTokenContext } from "./InvitationTokenContext.js";
import { InvitationTokenData } from "./InvitationTokenData.js";
import { useRefTokenQuery } from "./InvitationTokenProvider.generated.js";
import { resolveInvitationTokenData } from "./resolveInvitationTokenData.js";

export const InvitationTokenProvider = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const { token: urlToken, remove } = useUrlToken();
  const { token, tokenData, isLoading } = useInvitationToken(urlToken);

  return (
    <InvitationTokenContext.Provider
      value={{ token, tokenData, isLoading, remove }}
    >
      {children}
    </InvitationTokenContext.Provider>
  );
};

function useUrlToken(name: string = REF_TOKEN_URL_SEARCH_PARAM) {
  const { urlSearchParams, setUrlSearchParams } = useUrlSearchParams();
  const token = urlSearchParams.get(name);

  const remove = React.useCallback(() => {
    setUrlSearchParams({}, [name]);
  }, [name, setUrlSearchParams]);

  return { token, remove };
}

function useInvitationToken(refToken: string | null) {
  const client = useApolloClient();
  const query = useRefTokenQuery({
    client,
    variables: {
      // `skip` below keeps the query from running while this is null, so the
      // placeholder is never sent — a `!` assertion would claim a narrowing
      // the type does not carry.
      refToken: refToken ?? "",
    },
    skip: !refToken,
  });

  const tokenData: InvitationTokenData | null = refToken
    ? resolveInvitationTokenData(query.data)
    : null;

  const token = tokenData ? refToken : null;

  return {
    token,
    tokenData,
    isLoading: query.loading,
  };
}
